'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TurnstileProps } from 'react-turnstile';

import type { BoundTurnstileObject } from '@/components/DeferredTurnstile';
import { useTurnstileActivation } from '@/hooks/useTurnstileActivation';

type UseDeferredTurnstileTokenOptions = {
  enabled: boolean;
  siteKey: string | null;
  usesAlwaysPassKey: boolean;
};

export function useDeferredTurnstileToken({
  enabled,
  siteKey,
  usesAlwaysPassKey,
}: UseDeferredTurnstileTokenOptions) {
  const [turnstileInstanceKey, setTurnstileInstanceKey] = useState(0);
  const turnstileRef = useRef<BoundTurnstileObject | null>(null);
  const turnstileGenerationRef = useRef(0);
  const captchaResolveRef = useRef<((token: string) => void) | null>(null);
  const captchaRejectRef = useRef<((err: Error) => void) | null>(null);
  const captchaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captchaReadyWaitersRef = useRef<{
    resolve: (v: BoundTurnstileObject) => void;
    reject: (e: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
  } | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const captchaExecutionRef = useRef(false);
  const { activateTurnstile, isTurnstileActivated, turnstileActivationProps } =
    useTurnstileActivation(enabled);

  const clearCaptchaWaiters = useCallback(() => {
    if (captchaTimeoutRef.current) {
      clearTimeout(captchaTimeoutRef.current);
      captchaTimeoutRef.current = null;
    }
    captchaResolveRef.current = null;
    captchaRejectRef.current = null;
  }, []);

  const clearCaptchaExecution = useCallback(() => {
    captchaExecutionRef.current = false;
  }, []);

  const remountTurnstile = useCallback(() => {
    turnstileGenerationRef.current += 1;
    setTurnstileInstanceKey(turnstileGenerationRef.current);
  }, []);

  const resetCaptchaToken = useCallback(() => {
    turnstileRef.current?.reset();
    turnstileRef.current = null;
    captchaTokenRef.current = null;
    clearCaptchaWaiters();
    clearCaptchaExecution();
    if (enabled) {
      remountTurnstile();
    }
  }, [clearCaptchaExecution, clearCaptchaWaiters, enabled, remountTurnstile]);

  const waitForCaptchaReady = useCallback(async () => {
    if (!siteKey) {
      throw new Error('Captcha not configured');
    }
    if (turnstileRef.current) {
      return turnstileRef.current;
    }

    return new Promise<BoundTurnstileObject>((resolve, reject) => {
      if (captchaReadyWaitersRef.current) {
        clearTimeout(captchaReadyWaitersRef.current.timeout);
      }

      const timeout = setTimeout(() => {
        captchaReadyWaitersRef.current = null;
        reject(new Error('Captcha not ready'));
      }, 15000);

      captchaReadyWaitersRef.current = { resolve, reject, timeout };
    });
  }, [siteKey]);

  const requestCaptchaToken = useCallback(async () => {
    if (captchaTokenRef.current) {
      return captchaTokenRef.current;
    }

    await activateTurnstile();
    const boundTurnstile = await waitForCaptchaReady();

    return new Promise<string>((resolve, reject) => {
      clearCaptchaWaiters();
      captchaTimeoutRef.current = setTimeout(() => {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(new Error('Captcha timed out'));
      }, 8000);

      captchaResolveRef.current = (token: string) => {
        clearCaptchaWaiters();
        resolve(token);
      };
      captchaRejectRef.current = (err: Error) => {
        clearCaptchaWaiters();
        reject(err);
      };

      try {
        if (captchaTokenRef.current) {
          const existingToken = captchaTokenRef.current;
          clearCaptchaWaiters();
          resolve(existingToken);
          return;
        }

        if (!usesAlwaysPassKey && !captchaExecutionRef.current) {
          captchaExecutionRef.current = true;
          boundTurnstile.execute();
        }
      } catch (error) {
        clearCaptchaWaiters();
        clearCaptchaExecution();
        reject(error instanceof Error ? error : new Error('Captcha failed'));
      }
    });
  }, [
    activateTurnstile,
    clearCaptchaExecution,
    clearCaptchaWaiters,
    usesAlwaysPassKey,
    waitForCaptchaReady,
  ]);

  const handleCaptchaVerify = useCallback((generation: number, token: string) => {
    if (generation !== turnstileGenerationRef.current) {
      return;
    }

    captchaTokenRef.current = token;
    clearCaptchaExecution();
    captchaResolveRef.current?.(token);
    if (captchaReadyWaitersRef.current) {
      clearTimeout(captchaReadyWaitersRef.current.timeout);
      captchaReadyWaitersRef.current.resolve(turnstileRef.current as BoundTurnstileObject);
      captchaReadyWaitersRef.current = null;
    }
  }, [clearCaptchaExecution]);

  const handleCaptchaError = useCallback((generation: number, error?: unknown) => {
    if (generation !== turnstileGenerationRef.current) {
      return;
    }

    captchaTokenRef.current = null;
    clearCaptchaExecution();
    const err = error instanceof Error ? error : new Error('Captcha failed');
    captchaRejectRef.current?.(err);
    if (captchaReadyWaitersRef.current) {
      clearTimeout(captchaReadyWaitersRef.current.timeout);
      captchaReadyWaitersRef.current.reject(err);
      captchaReadyWaitersRef.current = null;
    }
  }, [clearCaptchaExecution]);

  useEffect(() => {
    turnstileRef.current = null;
    captchaTokenRef.current = null;
    clearCaptchaWaiters();
    clearCaptchaExecution();
  }, [clearCaptchaExecution, clearCaptchaWaiters, enabled, siteKey, usesAlwaysPassKey]);

  const turnstileGeneration = turnstileInstanceKey;

  const turnstileProps: TurnstileProps | null =
    enabled && siteKey && isTurnstileActivated
      ? {
          sitekey: siteKey,
          size: 'flexible',
          appearance: usesAlwaysPassKey ? 'always' : 'interaction-only',
          execution: usesAlwaysPassKey ? undefined : 'execute',
          onLoad: (_id, bound) => {
            if (turnstileGeneration !== turnstileGenerationRef.current) {
              return;
            }
            turnstileRef.current = bound;
            if (captchaReadyWaitersRef.current) {
              clearTimeout(captchaReadyWaitersRef.current.timeout);
              captchaReadyWaitersRef.current.resolve(bound);
              captchaReadyWaitersRef.current = null;
            }
          },
          onVerify: (token) => handleCaptchaVerify(turnstileGeneration, token),
          onError: (error) => handleCaptchaError(turnstileGeneration, error),
          onExpire: () => handleCaptchaError(turnstileGeneration, new Error('Captcha expired')),
          onTimeout: () => handleCaptchaError(turnstileGeneration, new Error('Captcha timed out')),
        }
      : null;

  return {
    requestCaptchaToken,
    resetCaptchaToken,
    turnstileActivationProps,
    turnstileKey: turnstileInstanceKey,
    turnstileProps,
  };
}
